import {useMemo} from 'react';
import moment from 'moment';

import type {
  DueWithinFilter,
  SkillPathRowData,
  SkillPathSortValue,
  SkillPathStatusSegment,
  SkillPathSubjectOption
} from './types';

/**
 * ⚠️ PLACEHOLDER DATA SOURCE — styling only.
 *
 * All filtering/sorting here is client-side mock behavior.
 */

interface Params {
  classroomId?: string;
  segment: SkillPathStatusSegment;
  search: string;
  subject: string;
  dueWithin: DueWithinFilter;
  dueDateStart: string;
  dueDateEnd: string;
  sort: {field: SkillPathSortValue; order: 'asc' | 'desc'};
}

interface Result {
  rows: SkillPathRowData[];
  counts: Record<SkillPathStatusSegment, number>;
  subjectOptions: SkillPathSubjectOption[];
  isLoading: boolean;
  hasError: boolean;
  /** No skill paths anywhere in this scope (drives the onboarding empty state). */
  isScopeEmpty: boolean;
}

const iso = (daysFromNow: number): string => moment().add(daysFromNow, 'days').toISOString();

const SUBJECTS = {
  ela: {id: 'subject-ela', name: 'ELA'},
  math: {id: 'subject-math', name: 'Math'},
  science: {id: 'subject-science', name: 'Science'},
  socialStudies: {id: 'subject-social-studies', name: 'Social Studies'},
  spanish: {id: 'subject-spanish', name: 'Spanish'}
};

// The adaptive subject catalog. Real impl sources this from the adaptive `subjects` query (the same
// list feeding useHomeAdaptiveSubjects / AdaptiveSkillPathsCard); the filter offers every adaptive
// subject, not only ones the teacher already has a path in.
const ADAPTIVE_SUBJECT_OPTIONS: SkillPathSubjectOption[] = [
  SUBJECTS.ela,
  SUBJECTS.math,
  SUBJECTS.science,
  SUBJECTS.socialStudies,
  SUBJECTS.spanish
];

// Representative rows spanning all three statuses and the multi-due-date range case.
const MOCK_ROWS: SkillPathRowData[] = [
  {
    id: 'skill-1',
    skillName: 'Solving Linear Equations',
    subjectId: SUBJECTS.math.id,
    subjectName: SUBJECTS.math.name,
    status: 'open',
    minStartDate: null,
    maxStartDate: null,
    minDueDate: iso(1),
    maxDueDate: iso(1),
    completedStudents: 11,
    totalStudents: 12
  },
  {
    id: 'skill-2',
    skillName: 'Factoring Quadratics',
    subjectId: SUBJECTS.math.id,
    subjectName: SUBJECTS.math.name,
    status: 'open',
    minStartDate: iso(-3),
    maxStartDate: iso(-3),
    minDueDate: iso(2),
    maxDueDate: iso(13),
    completedStudents: 6,
    totalStudents: 20
  },
  {
    id: 'skill-3',
    skillName: 'Central Idea & Theme',
    subjectId: SUBJECTS.ela.id,
    subjectName: SUBJECTS.ela.name,
    status: 'open',
    minStartDate: null,
    maxStartDate: null,
    minDueDate: iso(5),
    maxDueDate: iso(5),
    completedStudents: 18,
    totalStudents: 24
  },
  {
    id: 'skill-4',
    skillName: 'Cellular Respiration',
    subjectId: SUBJECTS.science.id,
    subjectName: SUBJECTS.science.name,
    status: 'scheduled',
    minStartDate: iso(3),
    maxStartDate: iso(3),
    minDueDate: iso(10),
    maxDueDate: iso(10),
    completedStudents: 0,
    totalStudents: 28
  },
  {
    id: 'skill-5',
    skillName: 'Ser vs. Estar',
    subjectId: SUBJECTS.spanish.id,
    subjectName: SUBJECTS.spanish.name,
    status: 'scheduled',
    minStartDate: iso(7),
    maxStartDate: iso(14),
    minDueDate: iso(21),
    maxDueDate: iso(28),
    completedStudents: 0,
    totalStudents: 15
  },
  {
    id: 'skill-6',
    skillName: 'Systems of Equations',
    subjectId: SUBJECTS.math.id,
    subjectName: SUBJECTS.math.name,
    status: 'closed',
    minStartDate: iso(-20),
    maxStartDate: iso(-20),
    minDueDate: iso(-5),
    maxDueDate: iso(-5),
    completedStudents: 22,
    totalStudents: 25
  },
  {
    id: 'skill-7',
    skillName: 'Author’s Purpose',
    subjectId: SUBJECTS.ela.id,
    subjectName: SUBJECTS.ela.name,
    status: 'closed',
    minStartDate: iso(-30),
    maxStartDate: iso(-30),
    minDueDate: iso(-12),
    maxDueDate: iso(-2),
    completedStudents: 19,
    totalStudents: 24
  }
];

// A class-scoped view shows a subset (mock), to demonstrate the scope difference visually.
const scopedRows = (classroomId?: string): SkillPathRowData[] =>
  classroomId ? MOCK_ROWS.filter((_row, index) => index % 2 === 0) : MOCK_ROWS;

const dueWithinBounds = (
  dueWithin: DueWithinFilter,
  customStart: string,
  customEnd: string
): {start: moment.Moment | null; end: moment.Moment | null} => {
  switch (dueWithin) {
    case 'week':
      return {start: moment().startOf('week'), end: moment().endOf('week')};
    case 'month':
      return {start: moment().startOf('month'), end: moment().endOf('month')};
    case 'custom':
      return {
        start: customStart ? moment(customStart).startOf('day') : null,
        end: customEnd ? moment(customEnd).endOf('day') : null
      };
    case 'schoolYear':
    default:
      return {start: null, end: null};
  }
};

const inDueWindow = (
  row: SkillPathRowData,
  bounds: {start: moment.Moment | null; end: moment.Moment | null}
): boolean => {
  const due = moment(row.minDueDate);
  if (bounds.start && due.isBefore(bounds.start)) {
    return false;
  }
  if (bounds.end && due.isAfter(bounds.end)) {
    return false;
  }
  return true;
};

const sortRows = (
  rows: SkillPathRowData[],
  sort: {field: SkillPathSortValue; order: 'asc' | 'desc'}
): SkillPathRowData[] => {
  const direction = sort.order === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (sort.field === 'name') {
      return a.skillName.localeCompare(b.skillName) * direction;
    }
    const aKey = sort.field === 'dueDate' ? a.minDueDate : (a.minStartDate ?? a.minDueDate);
    const bKey = sort.field === 'dueDate' ? b.minDueDate : (b.minStartDate ?? b.minDueDate);
    return (moment(aKey).valueOf() - moment(bKey).valueOf()) * direction;
  });
};

export const useSkillPathsList = ({
  classroomId,
  segment,
  search,
  subject,
  dueWithin,
  dueDateStart,
  dueDateEnd,
  sort
}: Params): Result => {
  return useMemo(() => {
    const all = scopedRows(classroomId);
    const bounds = dueWithinBounds(dueWithin, dueDateStart, dueDateEnd);
    const normalizedSearch = search.trim().toLowerCase();

    // Everything except the status segment — shared by the visible list and the tab counts.
    const preSegment = all.filter((row) => {
      if (subject !== 'all' && row.subjectId !== subject) {
        return false;
      }
      if (normalizedSearch && !row.skillName.toLowerCase().includes(normalizedSearch)) {
        return false;
      }
      return inDueWindow(row, bounds);
    });

    const counts = preSegment.reduce(
      (acc, row) => {
        acc[row.status] += 1;
        return acc;
      },
      {open: 0, closed: 0, scheduled: 0} as Record<SkillPathStatusSegment, number>
    );

    const rows = sortRows(
      preSegment.filter((row) => row.status === segment),
      sort
    );

    return {
      rows,
      counts,
      subjectOptions: ADAPTIVE_SUBJECT_OPTIONS,
      isLoading: false,
      hasError: false,
      isScopeEmpty: all.length === 0
    };
  }, [classroomId, segment, search, subject, dueWithin, dueDateStart, dueDateEnd, sort]);
};
